import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { Employee, EmployeeRole } from '../../types/auth';
import { ASSIGNABLE_EMPLOYEE_ROLES } from '../../types/auth';
import { ChangeRoleModal } from '../../components/modals/ChangeRoleModal';
import { roleLabels } from '../../utils/roles';

const CREATE_ROLES = ASSIGNABLE_EMPLOYEE_ROLES;

export function StaffSection() {
  const [staff, setStaff] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<EmployeeRole>('compliance_officer');
  const [creating, setCreating] = useState(false);

  const [roleTarget, setRoleTarget] = useState<Employee | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listEmployees();
      setStaff(res.employees);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.createEmployee({ name, email, password, role });
      if (!res.ok) throw new Error(res.error || 'Create failed');
      setSuccess(`Created ${res.employee.email}`);
      setName('');
      setEmail('');
      setPassword('');
      setRole('compliance_officer');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const saveRole = async (employeeId: string, newRole: EmployeeRole) => {
    const res = await api.patchEmployeeRole(employeeId, newRole);
    if (res.employee) {
      setStaff((list) => list.map((e) => (e.id === employeeId ? res.employee! : e)));
    } else {
      await load();
    }
  };

  const revoke = async (employee: Employee) => {
    if (employee.role === 'ceo') return;
    if (!window.confirm(`Revoke access for ${employee.email}?`)) return;
    setRevokingId(employee.id);
    setError(null);
    try {
      await api.deleteEmployee(employee.id);
      setStaff((list) => list.filter((e) => e.id !== employee.id));
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <>
      <div className="section-header">
        <h1 className="console-title">Staff Management</h1>
        <p className="console-sub">Internal team · CEO only</p>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}
      {success ? <p className="form-success section-error">{success}</p> : null}

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-title">Register new employee</div>
        <form className="employee-form-grid" onSubmit={(e) => void onCreate(e)}>
          <label className="login-label" htmlFor="new-name">
            Name
          </label>
          <input
            id="new-name"
            className="login-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <label className="login-label" htmlFor="new-email">
            Email
          </label>
          <input
            id="new-email"
            className="login-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label className="login-label" htmlFor="new-password">
            Password
          </label>
          <input
            id="new-password"
            className="login-input"
            type="password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <label className="login-label" htmlFor="new-role">
            Role
          </label>
          <select
            id="new-role"
            className="login-input"
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
          >
            {CREATE_ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabels[r] ?? r}
              </option>
            ))}
          </select>
          <button className="btn-primary" type="submit" disabled={creating} style={{ marginTop: 12 }}>
            {creating ? 'Creating…' : 'Create employee'}
          </button>
        </form>
      </div>

      <div className="section-title">Current staff</div>
      <div className="table-wrap">
        <div className="table-head data-table-head staff-head">
          <div className="th">Name</div>
          <div className="th">Email</div>
          <div className="th">Role</div>
          <div className="th col-actions">Actions</div>
        </div>
        {loading ? (
          <div className="table-row">
            <div className="col-span-all">Loading…</div>
          </div>
        ) : staff.length === 0 ? (
          <div className="table-row">
            <div className="col-span-all">No employees</div>
          </div>
        ) : (
          staff.map((emp) => (
            <div key={emp.id} className="table-row data-table-row staff-row">
              <div>{emp.name}</div>
              <div>{emp.email}</div>
              <div>{roleLabels[emp.role] ?? emp.role}</div>
              <div className="col-actions row-actions">
                <button
                  type="button"
                  className="btn-outline btn-sm"
                  disabled={emp.role === 'ceo'}
                  onClick={() => setRoleTarget(emp)}
                >
                  Change Role
                </button>
                <button
                  type="button"
                  className="btn-danger btn-sm"
                  disabled={emp.role === 'ceo' || revokingId === emp.id}
                  onClick={() => void revoke(emp)}
                >
                  {revokingId === emp.id ? 'Revoking…' : 'Revoke Access'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ChangeRoleModal
        employee={roleTarget}
        open={Boolean(roleTarget)}
        onClose={() => setRoleTarget(null)}
        onSave={saveRole}
      />
    </>
  );
}
